import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { api } from '@/services/api';
import { odooApi } from '@/services/api/index';
import uiReducer from '@/features/ui/uiSlice';
import i18nReducer from '@/features/i18n/i18nSlice';
import postsReducer from '@/features/posts/postsSlice';
import eventsReducer from '@/features/events/eventsSlice';
import talentsReducer from '@/features/talents/talentsSlice';

// Import Odoo hooks - this also injects endpoints into odooApi
// Must happen AFTER odooApi is imported
import '@/services/api/odoo-hooks';

/**
 * Redux Store Configuration
 * 
 * This store combines:
 * - RTK Query API reducer (for server state - both mock and Odoo)
 * - Feature slices (for client/UI state)
 * 
 * Structure:
 * - api: Mock RTK Query API state (events, media, etc.)
 * - odooApi: Real Odoo backend API state (news, talents, partners)
 * - ui: Global UI state (modals, notifications, etc.)
 * - i18n: Internationalization state
 * - posts: Posts feature UI state
 * - events: Events feature UI state
 * - talents: Talents feature UI state
 */
export const store = configureStore({
  reducer: {
    [api.reducerPath]: api.reducer,
    [odooApi.reducerPath]: odooApi.reducer,
    ui: uiReducer,
    i18n: i18nReducer,
    posts: postsReducer,
    events: eventsReducer,
    talents: talentsReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types
        ignoredActions: ['persist/PERSIST'],
      },
    }).concat(api.middleware, odooApi.middleware),
  devTools: import.meta.env.DEV,
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;






